import React, { forwardRef } from 'react';
import { styled } from '@stitches/react';
import ms from 'ms';
import { gql } from '@soundxyz/gql-string';
import type { CSS } from '@components-shared/src/stitches';
import { Background } from '@components/Song/Hero/Background';
import { Spinner } from '@components/basics/Spinner';
import { LetterLogo } from '@components/main-components/media/logo/Logo';
import type { ViralSoundsDownloadQueryQuery } from '@graphql/reactQuery';
import { useQuery, ViralSoundsDownloadQueryDocument } from '@graphql/reactQuery';
import { getStaticCoverImage } from '@utils/helpers';

const TITLE = 'The Viral 10: Sounds';

type AspectRatios = 'portrait' | 'landscape' | 'square';

/*#__PURE__*/ gql(/* GraphQL */ `
  query ViralSoundsDownloadQuery(
    $chartId: UUID!
    $pagination: ReleaseChartRankCursorConnectionArgs!
  ) {
    releaseChartById(chartId: $chartId) {
      id
      periodStartInclusive
      periodEndExclusive
      chartRanks(pagination: $pagination) {
        edges {
          node {
            id
            currentRank
            release {
              id
              title
              artist {
                id
                name
              }
              coverImage {
                id
                url
              }
              staticCoverImage {
                id
                url
              }
            }
          }
        }
      }
    }
  }
`);

const ViralTopTenHeading = ({
  type,
  periodStart,
  periodEnd,
}: {
  type: AspectRatios;
  periodStart: string;
  periodEnd: string;
}) => {
  const start = new Date(periodStart);
  const end = new Date(periodEnd);
  const monthStart = start.toLocaleString('en-GB', { month: 'short' });
  const monthEnd = end.toLocaleString('en-GB', { month: 'short' });
  const startDay = start.getDate();
  const endDay = end.getDate();
  const endYear = end.getFullYear();

  return (
    <>
      <Title type={type}>{TITLE}</Title>
      <Subtitle type={type}>
        Week of {monthStart} {startDay} - {monthEnd !== monthStart ? monthEnd : ''} {endDay},{' '}
        {endYear}
      </Subtitle>
    </>
  );
};

const Header = ({
  type,
  periodStart,
  periodEnd,
}: {
  type: AspectRatios;
  periodStart: string;
  periodEnd: string;
}) => {
  switch (type) {
    case 'landscape':
      return (
        <Row css={{ marginBottom: 16, width: '100%', alignItems: 'flex-start' }}>
          <LogoContainer type="landscape">
            <LogoWrapper size="small">
              <LetterLogo />
            </LogoWrapper>
          </LogoContainer>

          <Column css={{ gap: 8 }}>
            <ViralTopTenHeading type="landscape" periodStart={periodStart} periodEnd={periodEnd} />
          </Column>

          <LogoTextContainer>
            <LogoText type="landscape">Sound.xyz</LogoText>
          </LogoTextContainer>
        </Row>
      );
    default:
      return (
        <Column>
          <LogoContainer css={{ marginBottom: 48 }}>
            <LogoWrapper size="large">
              <LetterLogo />
            </LogoWrapper>
            <VerticalLine />
            <LogoText>Sound.xyz</LogoText>
          </LogoContainer>

          <Column css={{ gap: 16, marginBottom: 88 }}>
            <ViralTopTenHeading type="portrait" periodStart={periodStart} periodEnd={periodEnd} />
          </Column>
        </Column>
      );
  }
};

const ChartRow = ({
  currentRank,
  title,
  artist,
  type,
}: {
  currentRank: number;
  title: string;
  artist: string;
  type: AspectRatios;
}) => {
  return (
    <Row css={{ alignItems: 'center', gap: 8 }}>
      <Rank type={type}>{currentRank}</Rank>
      <Column css={{ minWidth: 0 }}>
        <ReleaseTitle type={type}>{title}</ReleaseTitle>
        <ReleaseArtist type={type}>{artist}</ReleaseArtist>
      </Column>
    </Row>
  );
};

const Charts = ({
  charts,
  type,
}: {
  charts: NonNullable<
    ViralSoundsDownloadQueryQuery['releaseChartById']
  >['chartRanks']['edges'][number][];
  type: AspectRatios;
}) => {
  const rowGap = type === 'square' ? 40 : 4;

  switch (type) {
    case 'portrait':
      return (
        <Column css={{ gap: 32 }}>
          {charts.map(chart => {
            const coverImage = chart.node.release.coverImage.url;
            const staticCoverImage = chart.node.release.staticCoverImage?.url;

            return (
              <Row key={chart.node.id} css={{ alignItems: 'center' }}>
                <Rank type="portrait">{chart.node.currentRank}</Rank>
                <CoverWrapper>
                  <Cover src={getStaticCoverImage({ coverImage, staticCoverImage })} />
                </CoverWrapper>
                <Column css={{ minWidth: 0 }}>
                  <ReleaseTitle type="portrait">{chart.node.release.title}</ReleaseTitle>
                  <ReleaseArtist type="portrait">{chart.node.release.artist.name}</ReleaseArtist>
                </Column>
              </Row>
            );
          })}
        </Column>
      );
    default:
      return (
        <Row css={{ gap: 40 }}>
          <Column css={{ gap: rowGap, minWidth: 0 }}>
            {charts.slice(0, 5).map(chart => {
              return (
                <ChartRow
                  key={chart.node.id}
                  currentRank={chart.node.currentRank}
                  title={chart.node.release.title}
                  artist={chart.node.release.artist.name || ''}
                  type={type}
                />
              );
            })}
          </Column>

          <Column css={{ gap: rowGap, minWidth: 0 }}>
            {charts.slice(5, 10).map(chart => {
              return (
                <ChartRow
                  key={chart.node.id}
                  currentRank={chart.node.currentRank}
                  title={chart.node.release.title}
                  artist={chart.node.release.artist.name || ''}
                  type={type}
                />
              );
            })}
          </Column>
        </Row>
      );
  }
};

export const ViralTopTen = forwardRef<HTMLDivElement, { chartId: string; type: AspectRatios }>(
  function ViralTopTen({ chartId, type }, ref) {
    const { data: charts, isLoading } = useQuery(ViralSoundsDownloadQueryDocument, {
      variables: {
        chartId,
        pagination: {
          first: 10,
          after: null,
        },
      },
      onError: error => {
        if (error) {
          // eslint-disable-next-line no-console
          console.error(error);
        }
      },
      staleTime: ms('10 minutes'),
      cacheTime: ms('15 minutes'),
    });

    const releaseChartById = charts?.data.releaseChartById;

    const firstRelease = releaseChartById?.chartRanks.edges[0]?.node.release;
    const periodStart = releaseChartById?.periodStartInclusive || '';
    const periodEnd = releaseChartById?.periodEndExclusive || '';
    const chartList = releaseChartById?.chartRanks.edges ?? [];

    if (isLoading) return <Spinner size="lg" />;

    return firstRelease ? (
      <Container ref={ref} size={type === 'landscape' ? 'small' : 'large'}>
        <StyledBackground
          coverImage={getStaticCoverImage({
            coverImage: firstRelease.coverImage.url,
            staticCoverImage: firstRelease.staticCoverImage?.url,
          })}
          fadeStyle="darkFade"
        />

        <Body>
          <Header type={type} periodStart={periodStart} periodEnd={periodEnd} />
          <Charts charts={chartList} type={type} />
        </Body>
      </Container>
    ) : null;
  },
);

const LogoWrapper = styled('div', {
  display: 'flex',
  alignItems: 'center',
  variants: {
    size: {
      large: {
        width: 104,
        height: 54,
      },
      small: {
        height: 60,
        marginRight: 40,
      },
    },
  },
});

const Body = styled('div', {
  zIndex: '$above1',
});

const VerticalLine = styled('div', {
  width: 4,
  height: 68,
  opacity: 0.28,
  backgroundColor: '$white',
});

const LogoText = styled('span', {
  fontFamily: '$title',
  fontWeight: 500,
  fontSize: 32,
  lineHeight: '40px',
  color: '$white',

  variants: {
    type: {
      landscape: {
        fontSize: 26,
        lineHeight: '36px',
      },
    },
  },
});

const Container = styled('div', {
  position: 'relative',
  minWidth: '100%',
  minHeight: '100%',
  display: 'flex',
  flexDirection: 'column',

  variants: {
    size: {
      large: {
        padding: '96px 68px',
      },
      small: {
        padding: '40px 48px',
      },
    },
  },
});

const Row = styled('div', {
  display: 'flex',
  width: '100%',
});

const Column = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',

  variants: {},
});

const LogoContainer = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: 24,
  svg: {
    width: '128px',
    height: '96px',
  },
  [`& svg`]: {
    width: '100%',
  },
  [`& path`]: {
    fill: '#fff',
  },

  variants: {
    type: {
      landscape: {
        svg: {
          width: '130px',
        },
      },
    },
  },
});

const Span = styled('span', {
  fontFamily: '$title',
  fontWeight: 500,
  color: '$white',
});

const Title = styled(Span, {
  fontSize: 98,
  lineHeight: '108px',

  variants: {
    type: {
      landscape: {
        fontSize: 44,
        lineHeight: '36px',
      },
      square: {},
      portrait: {},
    },
  },
});

const Subtitle = styled(Span, {
  fontSize: 44,
  lineHeight: '52px',

  variants: {
    type: {
      landscape: {
        fontSize: 20,
        lineHeight: '24px',
      },
      square: {},
      portrait: {},
    },
  },
});

const Rank = styled(Span, {
  minWidth: 70,
  textAlign: 'center',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  variants: {
    type: {
      square: {
        fontSize: 64,
        lineHeight: '80px',
        minHeight: 80,
      },
      landscape: {
        fontSize: 44,
        lineHeight: '52px',
        minHeight: 70,
      },
      portrait: {
        fontSize: 64,
        lineHeight: '80px',
        minHeight: 80,
      },
    },
  },
});

const CoverWrapper = styled('div', {
  marginLeft: 8,
  marginRight: 24,
  minWidth: 108,
  minHeight: 108,
});

const Cover = styled('img', {
  borderRadius: 16,
  maxWidth: 108,
  maxHeight: 108,
});

const textEllipsis: CSS = {
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  paddingRight: 8,
};

const ReleaseTitle = styled(Span, {
  ...textEllipsis,

  variants: {
    type: {
      portrait: {
        fontSize: 44,
        lineHeight: '52px',
      },
      square: {
        fontSize: 36,
        lineHeight: '44px',
      },
      landscape: {
        fontSize: 26,
        lineHeight: '36px',
      },
    },
  },
});

const ReleaseArtist = styled('span', {
  fontFamily: '$base',
  color: '$white',
  opacity: 0.5,

  variants: {
    type: {
      portrait: {
        fontSize: 28,
        lineHeight: '36px',
        fontWeight: 500,
      },
      square: {
        fontSize: 28,
        lineHeight: '36px',
        fontWeight: 500,
      },
      landscape: {
        fontSize: 16,
        lineHeight: '28px',
        fontWeight: 400,
      },
    },
  },
  ...textEllipsis,
});

const LogoTextContainer = styled('div', {
  marginLeft: 'auto',
});

const StyledBackground = styled(Background, {
  '.background': {
    '-webkit-background-size': '1080px 1920px;',
  },
});
